import { money } from "@/lib/api";
import { RentDueNudge } from "@/pages/renter/RentDueNudge";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Repeat, CheckCircle2, Send } from "lucide-react";

const money2 = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const centralNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "America/Chicago" }));
const currentRentPeriod = () => { const c = centralNow(); return `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`; };
const currentMonthName = () => centralNow().toLocaleDateString("en-US", { month: "long" });
const venmoRentLink = (lease) => {
  const handle = (lease.landlord_venmo || "").replace(/^@+/, "");
  const rem = lease.rent_this_period ? lease.rent_this_period.remaining : Number(lease.monthly_rent || 0);
  const amount = Number(rem || 0).toFixed(2);
  const note = `${currentMonthName()} Rent - ${lease.property_title}`;
  return `https://venmo.com/?txn=pay&recipients=${encodeURIComponent(handle)}&amount=${amount}&note=${encodeURIComponent(note)}`;
};

export const HomeCard = ({ signedLease, cardFee, openPay, toggleAutopay }) => {
  if (!signedLease) return null;
  return (
    <div className="mt-6 bg-forest text-white rounded-2xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">Your home</p>
          <h2 className="mt-1 text-2xl font-heading font-semibold">{signedLease.property_title}</h2>
          <p className="text-white/80 mt-1">{money(signedLease.monthly_rent)}/mo · Lease {signedLease.start_date} → {signedLease.end_date}</p>
          {signedLease.rent_this_period && signedLease.rent_this_period.paid > 0 && !signedLease.rent_this_period.fully_paid && (
            <p data-testid="rent-partial-status" className="text-sm text-white/90 mt-1.5 bg-white/10 rounded-lg px-3 py-1.5 inline-block">
              {currentMonthName()}: {money(signedLease.rent_this_period.paid)} paid of {money(signedLease.rent_this_period.monthly_amount)} · <span className="font-semibold">{money(signedLease.rent_this_period.remaining)} remaining</span>
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {signedLease.invoice_sent && !signedLease.invoice_paid && (
            <button data-testid="pay-invoice-btn" onClick={() => openPay({ purpose: "move_in_invoice", amount: signedLease.invoice_total, label: "Pay Move-in Invoice", lease_id: signedLease.id })} className="bg-white text-forest px-4 py-2.5 rounded-lg font-medium hover:bg-white/90 flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Pay Move-in Invoice {money(signedLease.invoice_total)}
            </button>
          )}
          {signedLease.invoice_paid && (
            <span data-testid="invoice-paid-badge" className="bg-white/15 text-white px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Move-in invoice paid</span>
          )}
          {!signedLease.invoice_sent && !signedLease.deposit_paid && (
            <button data-testid="pay-deposit-btn" onClick={() => openPay({ purpose: "security_deposit", amount: signedLease.security_deposit, label: "Pay Security Deposit", lease_id: signedLease.id })} className="bg-white text-forest px-4 py-2.5 rounded-lg font-medium hover:bg-white/90 flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Pay Deposit {money(signedLease.security_deposit)}
            </button>
          )}
          {(() => {
            const rs = signedLease.rent_this_period;
            const remaining = rs ? rs.remaining : Number(signedLease.monthly_rent || 0);
            const fully = rs ? rs.fully_paid : false;
            if (fully) {
              return (
                <span data-testid="rent-fully-paid-badge" className="bg-white/15 text-white px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> {currentMonthName()} rent fully paid</span>
              );
            }
            return (
              <button data-testid="pay-rent-btn" onClick={() => openPay({ purpose: "monthly_rent", amount: remaining, label: `Pay ${currentMonthName()} Rent`, lease_id: signedLease.id, allowPartial: true, maxAmount: remaining })} className="bg-terracotta text-white px-4 py-2.5 rounded-lg font-medium hover:bg-terracotta-hover flex items-center gap-2">
                <CreditCard className="h-4 w-4" /> {signedLease.autopay_enabled ? "Confirm Auto-Pay" : "Pay Rent"} {money(remaining)}
              </button>
            );
          })()}
          {signedLease.landlord_venmo && (
            <a data-testid="pay-venmo-btn" href={venmoRentLink(signedLease)} target="_blank" rel="noreferrer" className="bg-[#3D95CE] text-white px-4 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center gap-2">
              <Send className="h-4 w-4" /> Pay with Venmo
            </a>
          )}
        </div>
      </div>
      {signedLease.last_rent_paid_period === currentRentPeriod() && (
        <div data-testid="rent-recorded-badge" className="mt-3 inline-flex items-center gap-1.5 bg-white/15 text-white rounded-lg px-3 py-1.5 text-sm">
          <CheckCircle2 className="h-4 w-4" /> {currentMonthName()} rent recorded by your landlord{signedLease.last_rent_paid_method ? ` (${signedLease.last_rent_paid_method})` : ""}
        </div>
      )}
      <RentDueNudge lease={signedLease} />
      <div className="mt-5 pt-5 border-t border-white/15 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Repeat className="h-5 w-5 text-white/80" strokeWidth={1.75} />
          <div>
            <p className="text-sm font-medium">Auto-Pay rent</p>
            <p className="text-xs text-white/60">{signedLease.autopay_enabled ? "On — monthly reminder + one-click payment on the 1st." : "Turn this on so you never miss the 1st — we'll remind you and make paying one click."}</p>
            {signedLease.autopay_enabled && (
              <p data-testid="autopay-savings-note" className="text-[11px] text-white/70 mt-1.5 max-w-md">
                💡 Paying by card adds a {cardFee.percent}% + {money2(cardFee.flat)} processing fee (~{money2(signedLease.monthly_rent * cardFee.percent / 100 + cardFee.flat)}/mo). Pay by <span className="font-medium">PayPal or Venmo</span> instead and save about {money2((signedLease.monthly_rent * cardFee.percent / 100 + cardFee.flat) * 12)}/year.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/70">{signedLease.autopay_enabled ? "On" : "Off"}</span>
          <Switch data-testid={`autopay-${signedLease.id}`} checked={!!signedLease.autopay_enabled} onCheckedChange={() => toggleAutopay(signedLease)} className="data-[state=checked]:bg-white data-[state=unchecked]:bg-white/30" />
        </div>
      </div>
    </div>
  );
};
